import { createContext, CSSProperties, useLayoutEffect, useState } from 'react';
import { SurveyAnswersProps } from './surveyAnswers';
import { SurveyAnswersData } from './useSurveyAnswersData';
import Style from './surveyAnswers.module.css';
import RowStyle from './surveyTableRow.module.css';
import {
  minQuestionWidthPx,
  minTableWidthPx,
  singleAnswerWidthPx,
  slideWidthPx,
  tableSpacingWidthPx,
} from './constants';

export const SurveyAnswersColumnsContext = createContext(1);

export const useSurveyAnswersLayout = (
  answerCount: number,
  answerId: SurveyAnswersProps['answerId'],
  questionsList: SurveyAnswersData['questionsList'],
) => {
  const [swipeIndex, setSwipeIndex] = useState(0);
  const [tableElement, setTableElement] = useState<HTMLDivElement | null>(null);
  const [visibleColumns, setVisibleColumns] = useState(1);
  const [reservedWidth, setReservedWidth] = useState(minQuestionWidthPx + tableSpacingWidthPx);

  useLayoutEffect(() => {
    if (!tableElement) {
      return;
    }

    const updateVisibleColumns = () => {
      const rows = tableElement.querySelector<HTMLElement>(`.${Style.rows}`);
      const questionColumn = rows?.querySelector<HTMLElement>(`.${RowStyle.questionTextContainer}`);
      const lastColumn = rows?.querySelector<HTMLElement>(`.${RowStyle.lastColumn}`);
      if (!rows || !questionColumn || !lastColumn) {
        return;
      }

      const rowsLeft = rows.getBoundingClientRect().left;
      const leadingWidth = Math.max(
        ...Array.from(
          rows.querySelectorAll<HTMLElement>(`.${RowStyle.questionTextContainer}`),
          (column) => column.getBoundingClientRect().left - rowsLeft,
        ),
      );
      const questionWidth = parseFloat(getComputedStyle(questionColumn).minWidth);
      const reservedContentWidth = leadingWidth + questionWidth + lastColumn.offsetWidth;
      const scrollbarWidth = rows.offsetWidth - rows.clientWidth;
      const availableWidth = rows.clientWidth - reservedContentWidth;
      const capacity = Math.max(1, Math.floor(availableWidth / slideWidthPx));
      const nextVisibleColumns = answerId ? 1 : Math.min(Math.max(1, answerCount), capacity);

      setReservedWidth(reservedContentWidth + scrollbarWidth);
      setVisibleColumns(nextVisibleColumns);
      setSwipeIndex((current) => Math.min(current, Math.max(0, answerCount - nextVisibleColumns)));
    };

    updateVisibleColumns();
    const observer = new ResizeObserver(updateVisibleColumns);
    observer.observe(tableElement);
    const rows = tableElement.querySelector<HTMLElement>(`.${Style.rows}`);
    if (rows) {
      observer.observe(rows);
    }

    return () => observer.disconnect();
  }, [answerCount, answerId, questionsList, tableElement]);

  const answersWidth = answerId ? singleAnswerWidthPx : Math.min(answerCount, visibleColumns) * slideWidthPx;
  const preferredWidth = Math.max(
    minTableWidthPx,
    reservedWidth + (answerId ? singleAnswerWidthPx : answerCount * slideWidthPx),
  );
  const tableStyle = {
    width: preferredWidth,
    '--survey-answers-width': `${answersWidth}px`,
    '--survey-answer-width': `${answerId ? singleAnswerWidthPx : slideWidthPx}px`,
  } as CSSProperties;

  return { swipeIndex, setSwipeIndex, setTableElement, visibleColumns, tableStyle };
};
